// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** Error contracts for the capability host. Each maps 1:1 to a row in the
 *  ## Error Contracts table of CONTRACT-C3-CAPABILITY-PLUGIN.1.0.md.
 *  Codes are stable wire strings shared across tak-tdf and blindpipe. */

export type CapabilityErrorCode =
  | "CAPABILITY_ACTIVATION"
  | "UNKNOWN_CONTRIBUTION"
  | "PERMISSION_DENIED"
  | "CAPABILITY_CRASH"
  // --- 0.2a Platform hardening (additive; CONTRACT-C3 "Proposed evolution") ---
  | "MANIFEST_OVERREACH"
  | "INPUT_INVALID"
  | "HUMAN_GATE_REQUIRED"
  | "ACTOR_REFUSED";

/** Base class so callers can `instanceof CapabilityError` and branch on `.code`. */
export class CapabilityError extends Error {
  readonly code: CapabilityErrorCode;
  /** The capability id this error is scoped to (when known). */
  readonly capabilityId?: string;
  /** The original throw that the host caught and isolated, if any. */
  readonly cause?: unknown;

  constructor(
    code: CapabilityErrorCode,
    message: string,
    opts?: { capabilityId?: string; cause?: unknown },
  ) {
    super(message);
    this.name = new.target.name;
    this.code = code;
    this.capabilityId = opts?.capabilityId;
    this.cause = opts?.cause;
    // Restore prototype chain across the ES5 transpile boundary.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** `activate` throws/rejects; host registers none of its contributions. */
export class ErrCapabilityActivation extends CapabilityError {
  constructor(capabilityId: string, cause?: unknown) {
    super(
      "CAPABILITY_ACTIVATION",
      `capability "${capabilityId}" failed to activate`,
      { capabilityId, cause },
    );
  }
}

/** A `register*` targets a contribution kind unsupported by this host. */
export class ErrUnknownContribution extends CapabilityError {
  /** The unsupported contribution kind that was requested. */
  readonly kind: string;
  constructor(kind: string, capabilityId?: string) {
    super(
      "UNKNOWN_CONTRIBUTION",
      `unknown contribution kind "${kind}"`,
      { capabilityId },
    );
    this.kind = kind;
  }
}

/** `policy.decide` denies a declared permission/attribute → contribution refused. */
export class ErrPermissionDenied extends CapabilityError {
  /** The deny reason returned by policy.decide. */
  readonly reason: string;
  constructor(capabilityId: string, reason: string) {
    super(
      "PERMISSION_DENIED",
      `capability "${capabilityId}" denied by policy: ${reason}`,
      { capabilityId },
    );
    this.reason = reason;
  }
}

/** A capability callback throws at runtime; host isolates and tears down only
 *  that capability (host survives). */
export class ErrCapabilityCrash extends CapabilityError {
  constructor(capabilityId: string, cause?: unknown) {
    super(
      "CAPABILITY_CRASH",
      `capability "${capabilityId}" crashed at runtime and was isolated`,
      { capabilityId, cause },
    );
  }
}

// --- 0.2a Platform hardening (additive; CONTRACT-C3 "Proposed evolution") -------

/** A contribution exceeds what the capability's MANIFEST declares (an undeclared
 *  contribution kind, or a command whose id/actors over-reach). The host REFUSES the
 *  contribution (registers nothing for it) and logs THIS — the capability stays live
 *  (the crash-isolation contract is preserved). AOA §3a. */
export class ErrManifestOverreach extends CapabilityError {
  /** The refusal reason (which contribution over-reached, and how). */
  readonly reason: string;
  constructor(capabilityId: string, reason: string) {
    super(
      "MANIFEST_OVERREACH",
      `capability "${capabilityId}" over-reached its manifest: ${reason}`,
      { capabilityId },
    );
    this.reason = reason;
  }
}

/** A machine-supplied payload failed validation against a command's typed `input`
 *  schema; the host REFUSES the dispatch (run() is never called). AOA §3b. */
export class ErrInputInvalid extends CapabilityError {
  /** The command whose payload was rejected + the validation error. */
  readonly commandId: string;
  readonly reason: string;
  constructor(commandId: string, reason: string) {
    super("INPUT_INVALID", `command "${commandId}" rejected invalid input: ${reason}`);
    this.commandId = commandId;
    this.reason = reason;
  }
}

/** A command was dispatched by an actor class the manifest's `commandActors[id]`
 *  allow-list does NOT grant (the INVOCATION-time half of the actor gate — the
 *  registration-time half is ErrManifestOverreach). The host REFUSES the dispatch
 *  (run() is never called). AOA §3a/§3b: "the actor classes allowed to INVOKE each
 *  command", enforced at dispatch, not only at registration. */
export class ErrActorRefused extends CapabilityError {
  readonly commandId: string;
  constructor(commandId: string, detail: string) {
    super(
      "ACTOR_REFUSED",
      `command "${commandId}" refused for this actor class: ${detail}`,
    );
    this.commandId = commandId;
  }
}

/** A `risk:"irreversible"` command was dispatched without a satisfied HUMAN gate
 *  (the confirmation hook the caller must satisfy, enforced OUTSIDE any model). An
 *  ai-agent / npe actor can never self-confirm. AOA §2 (T-IRREVERSIBLE). */
export class ErrHumanGateRequired extends CapabilityError {
  readonly commandId: string;
  constructor(commandId: string, detail: string) {
    super(
      "HUMAN_GATE_REQUIRED",
      `irreversible command "${commandId}" requires a satisfied human gate: ${detail}`,
    );
    this.commandId = commandId;
  }
}
